import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, computed, inject, input, signal } from '@angular/core';

import { BetState } from '@bpos/v1/my-bets';
import { EpcotConfig, MyBetsConfig } from '@frontend/sports/common/client-config-data-access';
import { injectCurrentTheme } from '@frontend/sports/common/ui/shared';
import { EventSubscription } from '@frontend/sports/event-subscription/feature/base-subscription';

import { ScoreboardSubscriptionService } from '../../../event-subscription/scoreboard-subscription.service';
import { EarlyPayoutPickState } from '../../../my-bets-base/models/early-payout-types';
import { MyBetsEventModel } from '../../models/my-bets-event.model';
import { VisualizationBetState } from '../../models/my-bets-visualization-types';
import { MyBetsVisualizationService } from '../../services/my-bets-visualization.service';
import { BetBuilderLegState, MyBetsBet, MyBetsBetBuilderLeg } from './../../../my-bets-base/models/my-bets-viewmodels';

export interface IconDetails {
    icon: string;
    className?: string;
}
@Component({
    selector: 'ms-my-bets-multi-bet-card-indicator',
    templateUrl: 'my-bets-multi-bet-card-indicator.html',
    styleUrl: './my-bets-multi-bet-card-indicator.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [MyBetsVisualizationService],
})
export class MyBetsMultiBetCardIndicatorComponent implements OnInit, OnDestroy {
    result = input.required<BetState | BetBuilderLegState | EarlyPayoutPickState>();
    bet = input<MyBetsBet | null>(null);
    event = input<MyBetsEventModel | null>(null);
    myBetsBetBuilderLeg = input<MyBetsBetBuilderLeg>();

    private currentTheme = injectCurrentTheme();
    private isMgmTheme = ['mgm-4'].includes(this.currentTheme);

    liveBetStatusTrackingMarkets = inject(MyBetsConfig).liveBetStatusTrackingMarkets;

    betState = signal(VisualizationBetState.Open);

    indicatorDetails = computed(() => {
        const state = this.betState();
        if (state === VisualizationBetState.Open) {
            return this.getIndicatorDetails();
        } else {
            return this.getLiveIndicatorDetails(state);
        }
    });

    private pushSubscription: EventSubscription<MyBetsEventModel>;

    constructor(
        private epcotConfig: EpcotConfig,
        private myBetsVisualizationService: MyBetsVisualizationService,
        private pushService: ScoreboardSubscriptionService<MyBetsEventModel>,
    ) {}

    ngOnInit(): void {
        if (this.event()?.scoreboard?.started && (this.result() === BetState.Open || this.result() === BetBuilderLegState.Open)) {
            this.setBetState(this.bet(), this.event());
            this.subscribe();
        }
    }

    ngOnDestroy(): void {
        this.pushSubscription?.unsubscribe();
    }

    private setBetState(bet: MyBetsBet | null, event: MyBetsEventModel | null): void {
        if (bet && event?.fixtureScoreboard && this.isBetStatusEnabled(event, bet.sportId, bet.marketId ?? this.myBetsBetBuilderLeg()?.marketId)) {
            const state = this.myBetsVisualizationService.getLiveBetStatus(bet, event, this.myBetsBetBuilderLeg());
            this.betState.set(state);
        }
    }

    private isBetStatusEnabled(event: MyBetsEventModel, sportId: number, marketId: number): boolean {
        if (!sportId || !marketId) {
            return false;
        }
        const myBetMarketType = event?.marketStatInfo?.find((x) => x.marketId === marketId)?.marketType;

        return myBetMarketType !== undefined && this.liveBetStatusTrackingMarkets[sportId]?.includes(myBetMarketType);
    }

    private getIndicatorDetails(): IconDetails {
        if ((this.result() === EarlyPayoutPickState.Hidden || this.result() === EarlyPayoutPickState.Locked) && !this.isMgmTheme) {
            return this.epcotConfig.isEnabled ? { icon: 'theme-locked' } : { icon: 'theme-locked-i' };
        }
        switch (this.result()) {
            case BetState.Open:
            case BetBuilderLegState.Open:
                return { icon: 'theme-radio-off', className: 'indicator-open' };
            case BetState.Won:
            case BetState.Placed:
            case BetBuilderLegState.Won:
                return { icon: 'theme-success-i', className: 'indicator-won' };
            case BetState.Lost:
            case BetBuilderLegState.Lost:
                return { icon: 'theme-close-i', className: 'indicator-lost' };
            case BetState.WithHeld:
                return { icon: 'theme-not-available-i', className: 'indicator-lost' };
            case BetState.Canceled:
            case BetBuilderLegState.Cancelled:
                return { icon: 'theme-remove-i', className: 'indicator-cancelled' };
            default:
                return { icon: 'theme-radio-off', className: 'indicator-open' };
        }
    }

    private getLiveIndicatorDetails(betStatus: VisualizationBetState | undefined): IconDetails {
        switch (betStatus) {
            case VisualizationBetState.Winning:
                return { icon: 'theme-radio-on', className: 'indicator-winning' };
            case VisualizationBetState.Losing:
                return { icon: 'theme-radio-on', className: 'indicator-losing' };
            default:
                return { icon: 'theme-radio-off', className: 'indicator-open' };
        }
    }

    private onScoreboardUpdatedInternal = (): void => {
        this.setBetState(this.bet(), this.event());
    };

    private subscribe(): void {
        this.pushSubscription = this.pushService.subscribe([{ event: this.event()!, callback: this.onScoreboardUpdatedInternal }]);
    }
}
